<template>
  <div
      class="nx-editor advanced"
      ref="wrapper"
      v-click-outside="onClickOutside"
      v-scroll="fixToolbar">

    <div ref="editor"></div>

    <nx-quill-spelling-check-tool v-if="hasSpellingCheck"
                                  :field="name"
                                  :field-label="label"
                                  :quill="quill"
                                  :force-spellcheck-before-save="forceSpellcheckBeforeSave"
                                  :force-spellcheck-before-publish="forceSpellcheckBeforePublish" />

    <nx-search-replace-toolbar v-if="searchReplaceActive"
                               :active="searchReplaceActive"
                               :quill="quill" />

    <div class="nx-editor-sidebar"
        :style="sidebarStyle"
        ref="sidebar"
        v-show="sidebar.visible">
      <nx-editor-toolbar
          v-model="sidebar.value"
          :buttons="sidebarButtons"
          @click="onButtonClicked"
      />
    </div>

    <nx-fullscreen-dialog
      v-if="!modal"
      :active="fullscreen"
      :title="label"
      @dismiss="fullscreen = false"
    >
      <template slot="toolbar-items">
        <v-btn
          @click.native="fullscreen = false"
          color="accent"
          dark flat
          v-html="$t('message.common.close')"
        ></v-btn>
      </template>

      <v-container class="advanced-editor-modal">
        <nx-advanced-editor
          ref="modalEditor"
          modal
          :value="value"
          :type="type"
          :options="options"
          :name="name"
          :max-height="null"
          :sidebar-options="sidebarOptions"
          :hint="hint"
          :disabled="disabled"
          @input="handleFullscreenInput"
        />
        <div :class="{ ...counterClass, counter: true }">{{ counter }}</div>
      </v-container>
    </nx-fullscreen-dialog>

    <component
      v-if="blotDialog"
      v-bind="blotDialog.props"
      @close="blotDialog = null"
      :is="blotDialog.component"
      :quill="quill"
    />

    <component
      :active="dialog.active"
      :blot="dialog.blot"
      :is="dialog.component"
      :quill="quill"
      @dismiss="onDismiss"
      v-bind="{ ...dialog.props, ...dialog.fields }"
      v-if="dialog.component"
    />

    <nx-blot-dropdown v-if="showBlotsDropDown"
      :blots="blotsDropDownData.blots"
      :position="blotsDropDownData.position"
      :searcher="blotsDropDownData.searcher"
      @dismiss="showBlotsDropDown = false"
      @selected="onDropdownSelected"
    />

  </div>
</template>

<script>
import Quill from 'quill';
import { cloneDeep } from 'lodash';
import ClickOutside from 'vue-click-outside';
import config, { availableSidebarButtons } from '@/components/texteditor/config/advanced';
import QuillEventBus, { QuillEvents } from '@/components/texteditor/event-bus';
import NxSearchReplaceToolbar from '@/components/texteditor/advanced/SearchReplaceToolbar';
import NxBlotDropdown from '@/components/texteditor/advanced/BlotsDropDown';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import QuillEditor from '../QuillEditor';
import NxEditorToolbar from './EditorToolbar';
import ThesaurusSuggestions from '../mixins/thesaurus-suggestions';
import throttle from 'lodash/throttle';
import NxFullscreenDialog from '@/components/dialogs/FullscreenDialog';
import NxQuillSpellingCheckTool from '@/components/texteditor/spelling-check/SpellingCheckTool';
import { getScrollableParent } from '@/utils';

/**
 * AdvanceEditor Component
 *
 * Extend QuillEditor Component
 *
 * @property {String}  type  Change Text Editor type to 'advanced'
 */

const Block = Quill.import('blots/block');

export default {
  name: 'nx-advanced-editor',
  extends: QuillEditor,
  directives: {
    ClickOutside,
  },
  mixins: [ThesaurusSuggestions],
  props: {
    type: {
      type: String,
      default: 'advanced',
    },
    name: {
      type: String,
    },
    options: {
      type: Object,
      required: false,
      default: () => (config.sidebar),
    },
    sidebarOptions: {
      type: Array,
      required: false,
      default: () => ([]),
    },
    modal: {
      type: Boolean,
    },
  },
  components: {
    NxFullscreenDialog,
    NxQuillSpellingCheckTool,
    NxEditorToolbar,
    NxSearchReplaceToolbar,
    NxBlotDropdown,
  },
  data() {
    return {
      buttons: [],
      dialog: {
        active: false,
        component: null,
        blot: null,
        fields: {},
        props: {},
      },
      sidebar: {
        visible: false,
        value: false,
        top: 0,
        epsilon: 14,
      },
      searchReplaceActive: false,
      showBlotsDropDown: false,
      blotsDropDownData: {},
    };
  },
  computed: {
    sidebarStyle() {
      return {
        top: `${this.sidebar.top}px`,
      };
    },
    sidebarButtons() {
      return Object.entries(this.effectiveOptions)
        .map(([buttonId, button]) => ({
          ...button,
          id: buttonId,
        }))
        .filter(button => button.active);
    },
    availableOptions() {
      return {
        ...availableSidebarButtons,
        ...this.options,
      };
    },
    effectiveOptions() {
      if (this.sidebarOptions.length === 0) {
        return this.options;
      }
      const options = cloneDeep(this.availableOptions);
      return this.sidebarOptions
        .map((item) => {
          const component = isObject(item) ? item.component : item;
          if (!options[component]) {
            console.error(`Component ${component} is not present on quill configuration`);
            return null;
          }
          const props = isObject(item) ? item.props || {} : {};
          if (isEmpty(props)) {
            return options[component];
          }
          return {
            ...options[component],
            props: {
              ...(options[component].props || {}),
              ...props,
            },
          };
        })
        .filter(Boolean)
        .reduce((result, current) => {
          result[current.component.name] = current;
          return result;
        }, {});
    },
  },
  methods: {
    onClickOutside() {
      this.searchReplaceActive = false;
    },
    hideSidebar() {
      this.sidebar.visible = false;
      this.sidebar.value = false;
    },
    hooks() {
      this.quill.addContainer(this.$refs.sidebar);

      this.quill.on(Quill.events.EDITOR_CHANGE, (eventName, ...args) => {
        if (eventName !== Quill.events.SELECTION_CHANGE) {
          return;
        }
        if (!args[0]) {
          this.hideSidebar();
          return;
        }
        // se comprueba que se hace click en un elemento
        if (args[0].length === 0) {
          // se añade una nueva linea
          this.quill.insertText(args[0].index, '', '', '', 'api');
          this.quill.update();
        }
        this.showSidebar(args[0]);
      });

      this.$refs.editor.addEventListener('click', (e) => {
        if (this.sidebar.visible && this.sidebar.value) {
          const toolbarBound = this.$refs.sidebar.getBoundingClientRect();

          // horizontal offset
          if (toolbarBound.x + toolbarBound.width < e.pageX) {
            this.sidebar.value = false;
          }
        }
      });

      this.thesaurusAddEventListener();
    },
    getButtonEffectiveOptions(buttonId) {
      const option = this.availableOptions[buttonId];
      return this.effectiveOptions[option.component.name] || this.effectiveOptions[buttonId];
    },
    onBlotEdit(e) {
      e.stopPropagation();
      const blot = e.target;
      const buttonId = blot.className.replace('ql-embed', '').trim();
      this.openButtonDialog(this.getButtonEffectiveOptions(buttonId), blot, { ...blot.dataset });
    },
    onBlotRemove(e) {
      e.target.remove();
    },
    onButtonClicked(buttonId, button) {
      this.openButtonDialog(button);
    },
    onDismiss() {
      this.active = false;
      this.quill.enable(true);
      this.dialog = { active: false, component: null };
    },
    onKeyUp(event) {
      // If esc was pressed...
      if (event.keyCode === 27) {
        this.hideSidebar();
      }
    },
    showSidebar(range) {
      const block = this.quill.scroll.descendant(Block, range.index);
      this.hideSidebar();
      if (!range.length
        && block
        && block[0]
        && block[0].domNode.firstChild instanceof HTMLBRElement) {
        this.sidebar.top = this.quill.getBounds(range).top - this.sidebar.epsilon;
        this.sidebar.value = false;
        this.sidebar.visible = true;
      }
    },
    handleContainerScroll() {
      if (!this.sidebar.visible) {
        return;
      }
      const range = this.quill.getSelection();
      const top = this.quill.getBounds(range).top - this.sidebar.epsilon;
      this.sidebar.top = top;
      this.sidebar.visible = top >= 0 && top <= this.maxHeight;
    },
    onSearchReplace({ uuid }) {
      if (this.quill.uuid !== uuid) {
        return;
      }
      this.searchReplaceActive = !this.searchReplaceActive;
    },
    onDropdown({ uuid, params }) {
      if (this.quill.uuid !== uuid) {
        return;
      }

      this.blotsDropDownData = params.fields;
      this.showBlotsDropDown = true;
    },
    onDropdownSelected(blot) {
      if (this.effectiveOptions[blot.blotName]) {
        this.openButtonDialog(this.getButtonEffectiveOptions(blot.blotName), null);
      } else {
        this.quill.insertEmbed(this.blotsDropDownData.range.index, blot.blotName, '');
      }

      this.showBlotsDropDown = false;
    },
    openButtonDialog(button, blot = null, fields = {}) {
      this.sidebar.value = false;
      this.dialog = {
        active: false,
        blot,
        component: button.component,
        fields,
        props: button.props || {},
      };
      this.$nextTick(() => {
        this.dialog.active = true;
      });
    },
    addScrollListeners() {
      const container = this.quill.container.querySelector('.ql-editor');
      container.style.maxHeight = `${this.maxHeight}px`;
      this.throttleHandleContainerScroll = throttle(this.handleContainerScroll, 20);
      container.addEventListener('scroll', this.throttleHandleContainerScroll, { passive: true });
    },
    setScrollingContainer() {
      // Configure current scrolling container
      // @see https://quilljs.com/docs/configuration/#scrollingcontainer
      if (this.maxHeight) {
        this.quill.scrollingContainer = this.quill.container.querySelector('.ql-editor');
      }
      if (this.modal) {
        this.quill.scrollingContainer = getScrollableParent(this.$el);
      }
    },
  },
  mounted() {
    window.addEventListener('keyup', this.onKeyUp);
    QuillEventBus.$on(QuillEvents.SEARCH_REPLACE, this.onSearchReplace);
    QuillEventBus.$on(QuillEvents.DROPDOWN, this.onDropdown);
    this.$refs.wrapper.addEventListener('quill-embed-delete', this.onBlotRemove);
    this.$refs.wrapper.addEventListener('quill-embed-edit', this.onBlotEdit);
    if (this.maxHeight) {
      this.addScrollListeners();
    }
    this.setScrollingContainer();
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.onKeyUp);
    QuillEventBus.$off(QuillEvents.SEARCH_REPLACE);
    QuillEventBus.$off(QuillEvents.DROPDOWN);
    this.$refs.wrapper.addEventListener('quill-embed-delete', this.onBlotRemove);
    this.$refs.wrapper.addEventListener('quill-embed-edit', this.onBlotEdit);
    this.quill.container.querySelector('.ql-editor').removeEventListener('scroll', this.throttleHandleContainerScroll);
  },
};
</script>

<style lang="stylus">
@import '../../../assets/stylus/variables.styl'

.module-editor-sidebar {
  .nx-editor-sidebar {
    position: absolute;
    left: 0;
  }
}
.nx-editor-sidebar {
  margin-top: 2px;
  position: absolute;
  left: 0;
}
.advanced-editor-modal {
  background-color: $nx-primary--lighten-5;
  .counter {
    margin-top: 18px;
    color: $nx-primary--darken-1;
    &.input-group__counter--soft-limit-reached {
      color: $warning;
    }
    &.input-group__counter--recommended-min-reached {
      color: $success
    }
  }
  .ql-toolbar {
    background-color: $nx-primary--lighten-5;
    position: fixed;
    &:not(.affix) {
      width: auto !important;
    }
    &.affix {
      top: 24px;
    }
  }
  .ql-container {
    padding-top: 42px;
  }
}
.nx-editor.advanced {
  position: relative;
}

  @media $sm-and-down {
    .nx-editor-toolbar.v-toolbar {
      .v-btn {
        margin: 2px;
      }
    }
  }
</style>
